<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import KnowledgeBaseAPI from 'dashboard/api/knowledgeBase';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

import AddEditDialog from './components/AddEditDialog.vue';
import UploadDialog from './components/UploadDialog.vue';
import CrawlDialog from './components/CrawlDialog.vue';
import TestSearchDialog from './components/TestSearchDialog.vue';
import DeleteDialog from './components/DeleteDialog.vue';
import PersonaTab from './components/PersonaTab.vue';
import QualityTab from './components/QualityTab.vue';
import ConsoleEmbed from './components/ConsoleEmbed.vue';

const route = useRoute();

// 对话框模板引用
const addEditDialogRef = ref(null);
const uploadDialogRef = ref(null);
const crawlDialogRef = ref(null);
const testSearchDialogRef = ref(null);
const deleteDialogRef = ref(null);

// 当前选中的二级页签：kb（知识库） | persona（数字人） | quality（质量与标注）
const activeTab = ref('kb');

// 知识库数据状态
const items = ref([]);
const suggestions = ref([]);
const isLoading = ref(true);
const searchQuery = ref('');
const filterKind = ref('');
const filterEnabled = ref('');

const filterKindOptions = [
  { value: '', label: '全部知识类型' },
  { value: 'faq', label: 'FAQ（常见问答）' },
  { value: 'document', label: '文档片段' },
];

const filterEnabledOptions = [
  { value: '', label: '全部状态' },
  { value: 'true', label: '仅启用' },
  { value: 'false', label: '仅停用' },
];

const tabBarItems = computed(() => [
  { key: 'kb', label: '知识条目 (FAQ / 文档)', count: items.value.length },
  { key: 'persona', label: '数字人人设' },
  { key: 'quality', label: '质量与标注' },
]);

const activeTabIndex = computed(() => {
  const index = tabBarItems.value.findIndex((t) => t.key === activeTab.value);
  return index >= 0 ? index : 0;
});

const onTabChanged = (tab) => {
  activeTab.value = tab.key;
};

// 弹窗操作方法
const openAddDialog = () => {
  nextTick(() => {
    addEditDialogRef.value?.open();
  });
};

const openEditDialog = (item) => {
  nextTick(() => {
    addEditDialogRef.value?.open(item);
  });
};

const openDeleteDialog = (item) => {
  nextTick(() => {
    deleteDialogRef.value?.open(item);
  });
};

const openUploadDialog = () => {
  nextTick(() => {
    uploadDialogRef.value?.open();
  });
};

const openCrawlDialog = () => {
  nextTick(() => {
    crawlDialogRef.value?.open();
  });
};

const openTestSearchDialog = () => {
  nextTick(() => {
    testSearchDialogRef.value?.open();
  });
};

const resetFilters = () => {
  searchQuery.value = '';
  filterKind.value = '';
  filterEnabled.value = '';
};

// 兼容设置中的嵌入件页面（用量与账单 / 角色与权限）
const isSettingsEmbed = computed(() => {
  return (
    route.name === 'agent_console_usage_billing' ||
    route.name === 'agent_console_roles'
  );
});

// 加载知识库数据
const loadKbItems = async () => {
  isLoading.value = true;
  try {
    const res = await KnowledgeBaseAPI.getItems();
    items.value = res.data?.items || [];
  } catch (error) {
    useAlert(
      error.response?.data?.error || error.message || '加载知识库条目失败'
    );
  } finally {
    isLoading.value = false;
  }
};

// 加载待审建议
const loadSuggestions = async () => {
  try {
    const res = await KnowledgeBaseAPI.getSuggestions();
    suggestions.value = res.data?.suggestions || [];
  } catch (error) {
    // 建议加载失败不阻塞主界面
  }
};

// 采纳建议
const adoptSuggestion = async (id) => {
  try {
    await KnowledgeBaseAPI.adoptSuggestion(id);
    useAlert('已采纳为 FAQ 条目 ✓');
    await Promise.all([loadKbItems(), loadSuggestions()]);
  } catch (error) {
    useAlert(error.response?.data?.error || error.message || '采纳失败');
  }
};

// 忽略建议
const rejectSuggestion = async (id) => {
  try {
    await KnowledgeBaseAPI.rejectSuggestion(id);
    useAlert('已忽略该建议 ✓');
    await loadSuggestions();
  } catch (error) {
    useAlert(error.response?.data?.error || error.message || '操作失败');
  }
};

// 切换条目启用状态
const toggleItemEnabled = async (item) => {
  try {
    const nextState = !item.enabled;
    await KnowledgeBaseAPI.updateItem(item.id, { enabled: nextState });
    item.enabled = nextState;
    useAlert(nextState ? '知识条目已启用 ✓' : '知识条目已停用 ✓');
  } catch (error) {
    useAlert(error.response?.data?.error || error.message || '更新状态失败');
  }
};

// 过滤后的列表
const filteredItems = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  const kind = filterKind.value;
  const enabledStr = filterEnabled.value;

  return items.value.filter((i) => {
    if (kind && i.kind !== kind) return false;
    if (enabledStr !== '' && String(i.enabled) !== enabledStr) return false;
    if (q) {
      const matchTitle = (i.title || '').toLowerCase().includes(q);
      const matchContent = (i.content || '').toLowerCase().includes(q);
      return matchTitle || matchContent;
    }
    return true;
  });
});

onMounted(() => {
  if (!isSettingsEmbed.value) {
    loadKbItems();
    loadSuggestions();
  }
});

watch(
  () => route.name,
  () => {
    if (!isSettingsEmbed.value) {
      loadKbItems();
      loadSuggestions();
    }
  }
);
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-surface-1 overflow-y-auto">
    <!-- 如果是设置分组下的嵌入件（账单或权限），安全嵌入控制台 -->
    <div v-if="isSettingsEmbed" class="flex flex-1 w-full h-full">
      <ConsoleEmbed />
    </div>

    <!-- 原生知识库主体 -->
    <div v-else class="flex flex-col flex-1 max-w-7xl w-full mx-auto p-6 md:p-8 gap-6">
      <!-- 页面顶栏 -->
      <div class="flex flex-col gap-4 border-b border-n-weak pb-4">
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div class="flex items-center gap-3">
              <h1 class="text-xl font-bold tracking-tight text-n-slate-12">知识库</h1>
              <span class="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-n-alpha-2 text-n-slate-11">
                共 {{ items.length }} 条
              </span>
            </div>
            <p class="text-xs text-n-slate-10 mt-1">
              AI 严格依据此处的知识回答客户咨询。支持维护 <b>FAQ（常见问答对）</b> 与 <b>企业文档片段</b>。
            </p>
          </div>

          <!-- 顶部快捷操作按钮 -->
          <div v-if="activeTab === 'kb'" class="flex items-center gap-2 flex-wrap">
            <Button
              label="新建条目"
              icon="i-lucide-plus"
              size="sm"
              color="blue"
              @click="openAddDialog"
            />
            <Button
              label="上传文档"
              icon="i-lucide-upload"
              size="sm"
              variant="outline"
              color="slate"
              @click="openUploadDialog"
            />
            <Button
              label="抓取网页"
              icon="i-lucide-globe"
              size="sm"
              variant="outline"
              color="slate"
              @click="openCrawlDialog"
            />
            <Button
              label="检索测试"
              icon="i-lucide-search"
              size="sm"
              variant="outline"
              color="slate"
              @click="openTestSearchDialog"
            />
          </div>
        </div>

        <!-- 二级导航 Tabs -->
        <div class="pt-1">
          <TabBar
            :tabs="tabBarItems"
            :initial-active-tab="activeTabIndex"
            class="bg-n-alpha-black2"
            @tab-changed="onTabChanged"
          />
        </div>
      </div>

      <!-- Tab 1: 知识条目列表 -->
      <div v-if="activeTab === 'kb'" class="flex flex-col gap-5">
        <!-- 待审知识建议横幅（若有 pending 建议） -->
        <div
          v-if="suggestions.length > 0"
          class="p-4 border rounded-xl bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400 flex flex-col gap-3"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2 font-semibold text-sm">
              <Icon icon="i-lucide-lightbulb" class="size-4" />
              知识建议池（人工坐席修正 → 沉淀为 FAQ）：共有 {{ suggestions.length }} 条待采纳
            </div>
          </div>
          <div class="divide-y divide-amber-500/20">
            <div
              v-for="s in suggestions"
              :key="s.id"
              class="py-2.5 flex items-center justify-between gap-4 flex-wrap"
            >
              <div class="flex flex-col gap-0.5 max-w-2xl">
                <span class="text-xs font-semibold text-n-slate-12">客户提问：{{ s.question }}</span>
                <span class="text-xs text-amber-600 dark:text-amber-400">坐席回答：{{ s.answer }}</span>
              </div>
              <div class="flex items-center gap-2">
                <Button
                  label="采纳为 FAQ"
                  size="xs"
                  color="teal"
                  @click="adoptSuggestion(s.id)"
                />
                <Button
                  label="忽略"
                  size="xs"
                  variant="outline"
                  color="amber"
                  @click="rejectSuggestion(s.id)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- 搜索与筛选工具栏 -->
        <div class="flex items-center justify-between gap-3 flex-wrap">
          <div class="flex items-center gap-3 flex-wrap sm:flex-nowrap flex-1 min-w-0">
            <Input
              v-model="searchQuery"
              placeholder="搜索知识标题或正文内容…"
              class="group w-full sm:w-80 min-w-[220px] max-w-md [&>input]:ltr:!pl-8 [&>input]:rtl:!pr-8 [&>input]:!rounded-[0.625rem]"
              size="sm"
              type="search"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute top-1/2 -translate-y-1/2 text-n-slate-11 group-focus-within:text-n-brand size-3.5 ltr:left-2.5 rtl:right-2.5 pointer-events-none"
                />
              </template>
            </Input>

            <Select
              v-model="filterKind"
              :options="filterKindOptions"
              aria-label="条目类型筛选"
            />

            <Select
              v-model="filterEnabled"
              :options="filterEnabledOptions"
              aria-label="条目状态筛选"
            />
          </div>

          <span class="text-xs text-n-slate-10 whitespace-nowrap">
            显示 {{ filteredItems.length }} 条 / 共 {{ items.length }} 条
          </span>
        </div>

        <!-- 知识条目数据表格 -->
        <div class="border rounded-xl border-n-weak bg-n-solid-2 overflow-hidden shadow-sm">
          <table class="w-full text-left text-sm border-collapse">
            <thead class="bg-n-alpha-1 text-xs font-semibold text-n-slate-11 border-b border-n-weak">
              <tr>
                <th class="px-5 py-3">标题与内容摘要</th>
                <th class="px-5 py-3 w-28">类型</th>
                <th class="px-5 py-3 w-28">状态</th>
                <th class="px-5 py-3 text-right w-32">操作</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak text-n-slate-12">
              <!-- 加载中 -->
              <tr v-if="isLoading">
                <td colspan="4" class="px-5 py-12 text-center text-n-slate-10">
                  <div class="flex items-center justify-center gap-2">
                    <svg class="animate-spin size-5 text-n-brand" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    <span>正在加载知识库…</span>
                  </div>
                </td>
              </tr>

              <!-- 全库为空（无任何知识条目） -->
              <tr v-else-if="items.length === 0">
                <td colspan="4" class="px-5 py-14 text-center">
                  <div class="flex flex-col items-center justify-center max-w-sm mx-auto text-center">
                    <div class="p-3.5 rounded-full bg-n-alpha-2 text-n-slate-10 mb-3">
                      <svg class="size-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                        <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z"/>
                        <path d="M6 2v20"/>
                      </svg>
                    </div>
                    <h3 class="text-base font-semibold text-n-slate-12">暂无知识库条目</h3>
                    <p class="text-xs text-n-slate-10 mt-1 leading-relaxed">
                      AI 回答将严格基于您录入的内容。您可以添加常见问答（FAQ），或上传企业文档供 AI 阅读。
                    </p>
                    <div class="flex gap-2.5 mt-5">
                      <Button
                        label="新建知识条目"
                        icon="i-lucide-plus"
                        size="sm"
                        color="blue"
                        @click="openAddDialog"
                      />
                      <Button
                        label="上传文档"
                        icon="i-lucide-upload"
                        size="sm"
                        variant="outline"
                        color="slate"
                        @click="openUploadDialog"
                      />
                    </div>
                  </div>
                </td>
              </tr>

              <!-- 搜索/筛选无结果 -->
              <tr v-else-if="filteredItems.length === 0">
                <td colspan="4" class="px-5 py-12 text-center">
                  <div class="flex flex-col items-center justify-center max-w-sm mx-auto text-center">
                    <div class="p-3 rounded-full bg-n-alpha-2 text-n-slate-10 mb-2">
                      <Icon icon="i-lucide-search-x" class="size-6 text-n-slate-9" />
                    </div>
                    <h3 class="text-sm font-semibold text-n-slate-12">未找到匹配的知识条目</h3>
                    <p class="text-xs text-n-slate-10 mt-1">
                      当前筛选条件或关键词下无匹配结果，您可以清空条件后重试。
                    </p>
                    <div class="mt-4">
                      <Button
                        label="清空筛选条件"
                        variant="faded"
                        color="slate"
                        size="xs"
                        @click="resetFilters"
                      />
                    </div>
                  </div>
                </td>
              </tr>

              <!-- 数据行 -->
              <tr
                v-for="item in filteredItems"
                :key="item.id"
                class="hover:bg-n-alpha-1 transition-colors"
              >
                <!-- 标题与摘要 -->
                <td class="px-5 py-3.5">
                  <div class="flex flex-col gap-1 max-w-xl">
                    <span
                      class="font-semibold text-sm text-n-slate-12 cursor-pointer hover:text-n-brand"
                      @click.stop="openEditDialog(item)"
                    >
                      {{ item.title }}
                    </span>
                    <p class="text-xs text-n-slate-10 line-clamp-2 leading-relaxed">
                      {{ item.content }}
                    </p>
                    <span v-if="item.answer" class="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded w-fit mt-0.5">
                      参考答案：{{ item.answer }}
                    </span>
                  </div>
                </td>

                <!-- 类型 Badge -->
                <td class="px-5 py-3.5 whitespace-nowrap">
                  <span
                    class="px-2.5 py-0.5 text-xs font-medium rounded-full"
                    :class="item.kind === 'faq'
                      ? 'bg-n-brand/10 text-n-blue-11 border border-n-brand/20'
                      : 'bg-n-alpha-2 text-n-slate-11 border border-n-weak'"
                  >
                    {{ item.kind === 'faq' ? 'FAQ 问答' : '文档片段' }}
                  </span>
                </td>

                <!-- 启用状态切换 -->
                <td class="px-5 py-3.5 whitespace-nowrap">
                  <button
                    type="button"
                    class="reset-base inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full transition-colors cursor-pointer border-0 outline outline-1 !mb-0"
                    :class="item.enabled
                      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 outline-emerald-500/30 hover:bg-emerald-500/20'
                      : 'bg-n-alpha-2 text-n-slate-11 outline-n-weak hover:bg-n-alpha-3'"
                    @click.stop="toggleItemEnabled(item)"
                  >
                    <span class="size-1.5 rounded-full" :class="item.enabled ? 'bg-emerald-500' : 'bg-n-slate-9'" />
                    {{ item.enabled ? '已启用' : '已停用' }}
                  </button>
                </td>

                <!-- 操作按钮组 -->
                <td class="px-5 py-3.5 text-right whitespace-nowrap">
                  <div class="inline-flex items-center gap-1 justify-end">
                    <Button
                      icon="i-lucide-pencil"
                      size="xs"
                      variant="ghost"
                      color="slate"
                      title="编辑条目"
                      @click.stop="openEditDialog(item)"
                    />
                    <Button
                      icon="i-lucide-trash"
                      size="xs"
                      variant="ghost"
                      color="ruby"
                      title="删除条目"
                      @click.stop="openDeleteDialog(item)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Tab 2: 数字人人设 -->
      <div v-else-if="activeTab === 'persona'">
        <PersonaTab />
      </div>

      <!-- Tab 3: 质量与标注 -->
      <div v-else-if="activeTab === 'quality'">
        <QualityTab />
      </div>
    </div>

    <!-- 对话框群组 -->
    <AddEditDialog ref="addEditDialogRef" @saved="loadKbItems" />
    <UploadDialog ref="uploadDialogRef" @saved="loadKbItems" />
    <CrawlDialog ref="crawlDialogRef" @saved="loadKbItems" />
    <TestSearchDialog ref="testSearchDialogRef" />
    <DeleteDialog ref="deleteDialogRef" @deleted="loadKbItems" />
  </div>
</template>
